import React, { ReactNode, useEffect } from 'react'
import Cookies from 'universal-cookie'
import { addDays } from 'date-fns'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { SocietyInviteModalContent } from './SocietyInviteModalContent'
import { ModalFooter } from '../../../common/Modal/ModalFooter'
import { Button, ButtonVariant } from '../../../common/Button'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { CookieKeys } from '../../../../types/cookies'
import { IPreApprovedList } from '../../../../interfaces/models/societies.interfaces'
import { createRequest } from '../../../../api/requests'
import { deleteUserRole, societyQueries } from '../../../../api/societies'

interface SocietyInviteModalProps {
  preApproved: IPreApprovedList & { societyId: string }
  show: boolean
  close: () => void
}

export const SocietyInviteModal = ({
  show,
  close,
  preApproved,
}: SocietyInviteModalProps): ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { data: society } = useQuery(
    societyQueries.getSociety(preApproved.societyId)
  )
  const userId = useAuthenticatedUserId() as string
  const { translate } = useAppTranslation()
  const societyInviteViewedCookie = CookieKeys.SOCIETY_INVITE_VIEWED.replace(
    '{% id %}',
    society?._id || ''
  )

  const showSuccessMessage = (message: string): void => {
    setToastNotification(ToastType.SUCCESS, message)
  }

  const showErrorMessage = (message: string): void => {
    setToastNotification(ToastType.DANGER, message)
  }

  const { mutate: mutateCreateRequest, isPending: acceptLoading } = useMutation(
    {
      mutationFn: createRequest,
      onSuccess: () => {
        showSuccessMessage(
          translate('societyInviteModal.flashMessage.acceptPreApprovedSuccess')
        )
        close()
      },
      onError: () => {
        showErrorMessage(
          translate('societyInviteModal.flashMessage.acceptPreApprovedFailure')
        )
      },
    }
  )

  const { mutate: mutateDenyRequest, isPending: denyLoading } = useMutation({
    mutationFn: deleteUserRole,
    onSuccess: () => {
      showSuccessMessage(
        translate('societyInviteModal.flashMessage.denyPreApprovedSuccess')
      )
      close()
    },
    onError: () => {
      showErrorMessage(
        translate('societyInviteModal.flashMessage.denyPreApprovedFailure')
      )
    },
  })

  useEffect(() => {
    const setInviteSeen = async (): Promise<void> => {
      const cookies = new Cookies()
      cookies.set(societyInviteViewedCookie, true, {
        sameSite: true,
        secure: true,
        expires: addDays(new Date(), 30),
      })
    }
    setInviteSeen()
  }, [societyInviteViewedCookie])

  const onAcceptPress = async (): Promise<void> => {
    if (!society) return
    mutateCreateRequest({
      userId,
      societyId: society._id,
      unitId: preApproved?.unitId,
      role: preApproved?.role,
      message: 'preapproved',
    })
  }

  const onDeclinePress = async (): Promise<void> => {
    if (!society) return
    mutateDenyRequest([
      {
        societyId: society._id,
        unitIdOrTitle: preApproved?.unitId,
        userEmail: preApproved?.userEmail,
        unitRole: !!preApproved?.role,
      },
    ])
  }

  const buttonsDisabled = acceptLoading || denyLoading

  return (
    <Modal show={show} size="md">
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('societyInviteModal.title')}
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <SocietyInviteModalContent
              society={society}
              preApproved={preApproved}
            />
          </ModalBody>
        ),
        footer: (
          <ModalFooter>
            <Button
              onClick={onDeclinePress}
              loading={denyLoading}
              disabled={buttonsDisabled}
              label={translate('societyInviteModal.buttons.deny')}
              variant={ButtonVariant.DEFAULT}
            />
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={onAcceptPress}
              loading={acceptLoading}
              disabled={buttonsDisabled}
              label={translate('societyInviteModal.buttons.accept')}
            />
          </ModalFooter>
        ),
      }}
    </Modal>
  )
}
